import { AxleButton } from '@/components/buttons/AxleButton';
import { AxleCallout } from '@/components/displayFields/AxleCallout';
import { AxleSelectField } from '@/components/formFields/AxleSelectField';
import { AxleCheckboxField } from '@/components/formFields/checkboxes';
import { AxleConfirmDialog } from '@/components/layout/AxleConfirmDialogContent';
import { VerticalStack } from '@/components/utils/linearLayouts';
import { useBulkCancelVisits } from '@/components/visits/dialogs/useBulkCancelVisits';
import { queryClient } from '@/fetching/queryClient';
import { errorToast, successToast } from '@/toast';
import { useBooleanState } from '@/utils/booleanState';
import { useInputState, useJointInputState } from '@/utils/inputState';
import { reportError } from '@/utils/tracking';
import { ignoreVoidPromiseReturn } from '@/utils/typescriptHelpers';
import { gql } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { FC, useCallback, useMemo } from 'react';

import {
    CancelVisitDialogQuery,
    CancelVisitDialogQueryVariables,
} from './CancelVisitDialog.gql.types';

type CancelVisitDialogProps = {
    isOpen: boolean;
    onClose(): void;
    visitId: string;
};

const CANCEL_VISIT_DIALOG_QUERY = gql`
    query CancelVisitDialog($visitId: UUID!) {
        visit(id: $visitId) {
            id
            organization {
                id
                config {
                    id
                    communications {
                        id
                        shouldSendPatientAlertsUiToggleDefault
                    }
                }
            }
            patient {
                id
                permissions {
                    id
                    toggleCommunications
                }
            }
        }
        optionValueSet(type: APPOINTMENT_CANCELLATION_REASON) {
            values {
                id
                name
                sortOrder
            }
        }
    }
`;

export const CancelVisitDialog: FC<CancelVisitDialogProps> = (props) => {
    const { isOpen, onClose, visitId } = props;
    const client = useApolloClient();
    const { data } = useQuery<CancelVisitDialogQuery, CancelVisitDialogQueryVariables>(
        CANCEL_VISIT_DIALOG_QUERY,
        {
            variables: { visitId },
            skip: !isOpen,
        },
    );

    const cancellationReasons = useMemo(() => {
        if (!data?.optionValueSet?.values) {
            return [];
        }
        return [...data.optionValueSet.values].sort((a, b) => a.sortOrder - b.sortOrder);
    }, [data]);

    const requiresCancellationReason = cancellationReasons.length > 0;

    const cancellationReason = useInputState<string>('', {
        required: requiresCancellationReason,
    });

    const sendPatientCancelAlert = useInputState<boolean>(
        data?.visit?.organization.config.communications.shouldSendPatientAlertsUiToggleDefault ??
            false,
    );
    const canToggleComms = data?.visit?.patient.permissions.toggleCommunications ?? false;
    const patientId = data?.visit?.patient.id;

    const { checkValid } = useJointInputState([cancellationReason, sendPatientCancelAlert]);

    // `loading` flips synchronously when the mutation fires, so we drive the
    // disabled state off it rather than a separate flag — this is what keeps
    // the button from being clicked again while the request is in flight.
    const [bulkCancelVisits, { loading }] = useBulkCancelVisits();

    const onConfirm = useCallback(async () => {
        if (!checkValid()) {
            return;
        }
        const reasonUuid =
            cancellationReason.value.length === 0 ? undefined : cancellationReason.value;
        try {
            const { data: result } = await bulkCancelVisits({
                variables: {
                    input: {
                        visits: [
                            {
                                visitId,
                                ...(reasonUuid ? { cancellationReasonUuid: reasonUuid } : {}),
                            },
                        ],
                        shouldNotifyPatients: canToggleComms ? sendPatientCancelAlert.value : null,
                    },
                },
            });
            if (!result?.bulkCancelVisits.success) {
                const messages = (result?.bulkCancelVisits.results ?? [])
                    .flatMap((r) => r.errors ?? [])
                    .map((e) => e.message);
                errorToast(
                    messages.length
                        ? `Failed to cancel the visit: ${messages.join(', ')}`
                        : 'Failed to cancel the visit. Please retry.',
                    { autoClose: false },
                );
                return;
            }
            successToast('Canceled the visit!');
        } catch {
            errorToast('Failed to cancel the visit. Please retry.');
            return;
        }

        // The visit lives in two caches. Refresh the GraphQL surfaces via Apollo…
        client.cache.evict({ fieldName: 'visits' });
        // …best-effort: the cancel already succeeded, so a failed refetch is reported for
        // observability rather than surfaced as a user-facing error.
        client.refetchQueries({ include: 'active' }).catch(reportError);
        // …and mirror the REST (React Query) invalidations the old REST cancel
        // hook performed, so REST-backed views (visit changelog, schedule,
        // patient data) don't show stale data after the GraphQL cancel.
        void queryClient.resetQueries(['visit', visitId]);
        void queryClient.invalidateQueries(['visits']);
        void queryClient.invalidateQueries(['schedule']);
        void queryClient.invalidateQueries(['visit-changelog', visitId]);
        void queryClient.invalidateQueries(['visit-requests']);
        void queryClient.invalidateQueries(['service-orders']);
        if (patientId) {
            void queryClient.invalidateQueries(['patient', patientId]);
        }
        onClose();
    }, [
        bulkCancelVisits,
        cancellationReason.value,
        checkValid,
        client,
        onClose,
        sendPatientCancelAlert.value,
        canToggleComms,
        visitId,
        patientId,
    ]);

    return (
        <AxleConfirmDialog
            isOpen={isOpen}
            close={onClose}
            confirmCta={'Confirm'}
            cancelCta={'Back'}
            onConfirm={ignoreVoidPromiseReturn(onConfirm)}
            confirmButtonEnabled={!loading}
            cancelButtonProps={{ disabled: loading }}
            title='Cancel Visit?'
        >
            Are you sure you want to cancel this visit?
            <AxleCallout type='warning'>This will cancel the visit in the system.</AxleCallout>
            <VerticalStack spacing={2}>
                {cancellationReasons.length > 0 && (
                    <AxleSelectField
                        label='Cancellation Reason'
                        inputState={cancellationReason}
                        options={cancellationReasons}
                        optionToValue={(reason) => reason.id}
                        optionToLabel={(reason) => reason.name}
                    />
                )}
                {canToggleComms && (
                    <AxleCheckboxField
                        checkboxText='Notify patient of cancellation?'
                        inputState={sendPatientCancelAlert}
                    />
                )}
            </VerticalStack>
        </AxleConfirmDialog>
    );
};

type CancelButtonProps = {
    visitId: string;
};

export const CancelVisitButton: FC<CancelButtonProps> = (props) => {
    const { visitId } = props;
    const [
        isCancelConfirmationDialogOpen,
        openCancelConfirmationDialog,
        closeCancelConfirmationDialog,
    ] = useBooleanState(false);

    return (
        <>
            <CancelVisitDialog
                isOpen={isCancelConfirmationDialogOpen}
                onClose={closeCancelConfirmationDialog}
                visitId={visitId}
            />
            <AxleButton onClick={openCancelConfirmationDialog}>Cancel</AxleButton>
        </>
    );
};
